import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Loader2, Upload, X as XIcon, Eye, ShieldCheck, FileText,
  Image as ImageIcon, Sparkles, Send, Plus, Mail,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GABY_AVATAR = 'https://customer-assets.emergentagent.com/job_chantier-mobile-1/artifacts/01h3kcfg_1000021463.jpg';
const GABY_COLOR = '#F59E0B';

function SendEmailDialog({ open, analysis, onClose }) {
  const [emails, setEmails] = useState(['']);
  const [messagePerso, setMessagePerso] = useState('');
  const [sending, setSending] = useState(false);

  if (!open || !analysis) return null;

  const updateEmail = (idx, value) => setEmails(prev => prev.map((e, i) => i === idx ? value : e));
  const addEmail = () => setEmails(prev => prev.length < 10 ? [...prev, ''] : prev);
  const removeEmail = (idx) => setEmails(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const submit = async () => {
    const clean = emails.map(e => e.trim()).filter(Boolean);
    if (clean.length === 0) { toast.error('Au moins 1 email requis'); return; }
    const invalid = clean.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid) { toast.error(`Email invalide: ${invalid}`); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/boris/send-email`, {
        analysis_id: analysis.id,
        recipients: clean,
        message_perso: messagePerso || undefined,
      }, { withCredentials: true, timeout: 60000 });
      if (r.data.sent_count === r.data.total_recipients) {
        toast.success(`Envoyé à ${r.data.sent_count} destinataire(s)`);
      } else {
        toast.warning(`Partiel : ${r.data.sent_count}/${r.data.total_recipients} envoyés. Echecs: ${(r.data.failed_recipients || []).join(', ')}`);
      }
      setEmails(['']); setMessagePerso('');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid="boris-send-dialog">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6">
        <h2 className="text-xl font-bold text-[#111827] mb-1 flex items-center gap-2">
          <Mail size={20} className="text-amber-500" /> Envoyer par e-mail
        </h2>
        <p className="text-sm text-[#6B7280] mb-5">
          Partagez cette analyse Gaby avec le maître d&apos;ouvrage, le maître d&apos;œuvre, les entreprises ou tout autre intervenant du chantier.
        </p>

        <label className="block text-sm font-semibold text-[#111827] mb-1.5">Destinataires (max 10) *</label>
        <div className="space-y-2 mb-3">
          {emails.map((email, idx) => (
            <div key={idx} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={e => updateEmail(idx, e.target.value)}
                placeholder={idx === 0 ? 'mo@maitre-ouvrage.fr' : 'autre@email.fr'}
                data-testid={`boris-send-email-${idx}`}
                className="flex-1"
              />
              {emails.length > 1 && (
                <button onClick={() => removeEmail(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded"
                        data-testid={`boris-send-remove-${idx}`}>
                  <XIcon size={16} />
                </button>
              )}
            </div>
          ))}
          {emails.length < 10 && (
            <button onClick={addEmail} className="text-xs text-[#0055FF] flex items-center gap-1 hover:underline"
                    data-testid="boris-send-add-email">
              <Plus size={12} /> Ajouter un destinataire
            </button>
          )}
        </div>

        <label className="block text-sm font-semibold text-[#111827] mb-1.5">Message personnel (optionnel)</label>
        <Textarea
          rows={3}
          value={messagePerso}
          onChange={e => setMessagePerso(e.target.value)}
          maxLength={1000}
          placeholder="Ex: Bonjour, suite à la visite de ce matin, voici l'analyse Gaby. Merci de tenir compte des points critiques avant la prochaine réunion."
          data-testid="boris-send-message"
        />
        <p className="text-[10px] text-gray-400 mt-1">{messagePerso.length}/1000 caractères</p>

        <div className="bg-gray-50 border rounded-lg p-3 mt-4 text-xs text-gray-600">
          📧 L&apos;email contiendra l&apos;analyse Gaby complète au format HTML, avec votre nom comme expéditeur visible et un disclaimer rappelant que la responsabilité finale revient au coordonnateur SPS.
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="boris-send-cancel">Annuler</Button>
          <Button onClick={submit} disabled={sending}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="boris-send-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send size={14} className="mr-2" />Envoyer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}



// Minimal markdown -> HTML
function MarkdownView({ content }) {
  if (!content) return null;
  const html = content
    .replace(/^### (.*)$/gm, '<h3 class="text-base font-bold mt-3 mb-1 text-[#111827]">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2 text-[#111827]">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\- (.*)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^([0-9]+)\. (.*)$/gm, '<li class="ml-4 list-decimal">$2</li>')
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
    .replace(/\n\n/g, '</p><p class="mt-2">');
  return <div className="prose prose-sm max-w-none text-[#111827] leading-relaxed" dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }} />;
}

// Consomme un flux SSE (text/event-stream) et applique progressivement les
// deltas a la state via setter(prev => ...). A la reception de l'event "done",
// remplace l'objet complet (avec id MongoDB) pour permettre l'envoi par e-mail.
async function consumeSSE(response, setter) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE delimit les events par \n\n
    let sepIdx;
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      if (!rawEvent.trim()) continue;

      let eventName = 'message';
      let dataStr = '';
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
      }
      if (!dataStr) continue;

      let payload;
      try { payload = JSON.parse(dataStr); } catch { continue; }

      if (eventName === 'delta' && payload.text) {
        setter(prev => prev ? { ...prev, result_markdown: (prev.result_markdown || '') + payload.text } : prev);
      } else if (eventName === 'done') {
        setter(() => ({ ...payload, streaming: false }));
      } else if (eventName === 'error') {
        throw new Error(payload.error || 'Erreur Gaby streaming');
      }
    }
  }
}

function PhotoPreview({ file, onRemove, index }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 group" data-testid={`boris-photo-${index}`}>
      {url && <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />}
      <button onClick={onRemove} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition" data-testid={`boris-photo-remove-${index}`}>
        <XIcon size={12} />
      </button>
    </div>
  );
}

export function BorisChantierPage() {
  const { chantierId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('analyze');  // analyze | synthese | history
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [synthese, setSynthese] = useState(null);
  const [synthLoading, setSynthLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sendDialogFor, setSendDialogFor] = useState(null);

  useEffect(() => {
    // Ping Gaby status juste pour verifier l'auth avant d'afficher le contenu
    axios.get(`${API}/boris/status`, { withCredentials: true })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFiles = (files) => {
    const arr = Array.from(files).slice(0, 5 - photos.length);
    const validated = arr.filter(f => {
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(f.type)) {
        toast.error(`${f.name}: format non supporté (JPEG/PNG/WEBP uniquement)`);
        return false;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name}: trop volumineux (max 8 Mo)`);
        return false;
      }
      return true;
    });
    setPhotos(prev => [...prev, ...validated].slice(0, 5));
  };

  const analyze = async () => {
    if (photos.length === 0) { toast.error('Ajoutez au moins 1 photo'); return; }
    setSubmitting(true);
    setResult({
      id: null,
      type: 'analyze',
      result_markdown: '',
      created_at: new Date().toISOString(),
      streaming: true,
    });
    const fd = new FormData();
    fd.append('chantier_id', chantierId);
    fd.append('notes_utilisateur', notes);
    photos.forEach(p => fd.append('photos', p));
    try {
      const r = await fetch(`${API}/boris/analyze-stream`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!r.ok) {
        const errText = await r.text().catch(() => '');
        throw new Error(errText || `HTTP ${r.status}`);
      }
      await consumeSSE(r, setResult);
      toast.success('Analyse Gaby terminée');
    } catch (e) {
      setResult(null);
      const msg = e?.response?.data?.detail || e?.message || 'Gaby n\'a pas pu analyser';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const loadSynthese = useCallback(async () => {
    setSynthLoading(true);
    setSynthese({
      id: null,
      type: 'synthese',
      result_markdown: '',
      created_at: new Date().toISOString(),
      streaming: true,
    });
    try {
      const r = await fetch(`${API}/boris/synthese-stream/${chantierId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!r.ok) {
        const errText = await r.text().catch(() => '');
        throw new Error(errText || `HTTP ${r.status}`);
      }
      await consumeSSE(r, setSynthese);
    } catch (e) {
      setSynthese(null);
      const msg = e?.response?.data?.detail || e?.message || 'Synthèse impossible';
      toast.error(msg);
    } finally { setSynthLoading(false); }
  }, [chantierId]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/boris/history/${chantierId}`, { withCredentials: true });
      setHistory(r.data.analyses || []);
    } catch { /* noop */ }
  }, [chantierId]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  if (loading) return <AppShell><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div></AppShell>;

  return (
    <AppShell>
      <div className="space-y-4" data-testid="boris-chantier-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => navigate(-1)} data-testid="boris-back-btn">
            <ArrowLeft size={22} />
          </Button>
          <img src={GABY_AVATAR} alt="Gaby" className="w-14 h-14 rounded-full border-2 shadow-sm" style={{ borderColor: GABY_COLOR, background: '#fff' }} />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827]">
              Gaby
            </h1>
            <p className="text-sm text-[#4B5563]">Expert IA Coordination SPS · Analyse photos · Observations · Synthèse</p>
          </div>
        </div>

        {/* Gaby accessible a tous les utilisateurs authentifies (sorti de beta) */}
        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[
            { id: 'analyze', label: 'Analyse photos', icon: ImageIcon },
            { id: 'synthese', label: 'Synthèse chantier', icon: Sparkles },
            { id: 'history', label: 'Historique', icon: FileText },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`boris-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition border-b-2 ${tab === t.id ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

            {/* Analyze */}
            {tab === 'analyze' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border shadow-sm p-4">
                  <label className="text-sm font-semibold text-[#111827] mb-2 block">Photos du chantier (1-5)</label>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    {photos.map((p, i) => (
                      <PhotoPreview key={i} index={i} file={p} onRemove={() => setPhotos(prev => prev.filter((_, j) => j !== i))} />
                    ))}
                    {photos.length < 5 && (
                      <label className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition"
                             data-testid="boris-upload-zone">
                        <Upload size={20} className="text-gray-400" />
                        <span className="text-[10px] text-gray-500 mt-1">Ajouter</span>
                        <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden"
                               onChange={(e) => handleFiles(e.target.files)} data-testid="boris-photo-input" />
                      </label>
                    )}
                  </div>

                  <label className="text-sm font-semibold text-[#111827] mb-1.5 block">Notes du coordonnateur (optionnel)</label>
                  <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Ex: Phase gros œuvre, intervention sur le lot 03..." maxLength={1000}
                            data-testid="boris-notes-input" />

                  <Button onClick={analyze} disabled={submitting || photos.length === 0}
                          className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                          data-testid="boris-analyze-btn">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Gaby analyse les photos…</> : <><Eye size={16} className="mr-2" />Lancer l&apos;analyse Gaby</>}
                  </Button>
                </div>

                {result && (
                  <div className="bg-white rounded-2xl border shadow-sm p-5" data-testid="boris-result">
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                      <img src={GABY_AVATAR} alt="Gaby" className="w-8 h-8 rounded-full border" style={{ borderColor: GABY_COLOR }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold" style={{ color: GABY_COLOR }}>
                          Gaby · Observation CSPS
                          {result.streaming && <span className="ml-2 text-[10px] font-semibold text-amber-600 animate-pulse" data-testid="boris-streaming-badge">● rédige en direct</span>}
                        </p>
                        <p className="text-[10px] text-gray-400">{new Date(result.created_at).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                    <div data-testid="boris-result-markdown">
                      <MarkdownView content={result.result_markdown} />
                      {result.streaming && <span className="inline-block w-2 h-4 bg-amber-500 align-middle ml-0.5 animate-pulse" data-testid="boris-cursor" />}
                    </div>
                    <div className="mt-4 pt-3 border-t flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded flex-1">
                        <ShieldCheck size={14} />
                        Cette analyse est une aide à la décision. Le coordonnateur reste responsable de la validation finale.
                      </div>
                      <Button onClick={() => setSendDialogFor(result)}
                              disabled={result.streaming || !result.id}
                              className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0 disabled:opacity-50"
                              data-testid="boris-send-result-btn">
                        <Mail size={14} className="mr-1" /> Envoyer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Synthese */}
            {tab === 'synthese' && (
              <div className="space-y-4">
                <Button onClick={loadSynthese} disabled={synthLoading} className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="boris-synthese-btn">
                  {synthLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Gaby construit la synthèse…</> : <><Sparkles size={16} className="mr-2" />Générer la synthèse du chantier</>}
                </Button>
                {synthese && (
                  <div className="bg-white rounded-2xl border shadow-sm p-5" data-testid="boris-synthese-result">
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                      <img src={GABY_AVATAR} alt="Gaby" className="w-8 h-8 rounded-full border" style={{ borderColor: GABY_COLOR }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold" style={{ color: GABY_COLOR }}>
                          Gaby · Synthèse globale
                          {synthese.streaming && <span className="ml-2 text-[10px] font-semibold text-amber-600 animate-pulse">● rédige en direct</span>}
                        </p>
                        <p className="text-[10px] text-gray-400">{new Date(synthese.created_at).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                    <div data-testid="boris-synthese-markdown">
                      <MarkdownView content={synthese.result_markdown} />
                      {synthese.streaming && <span className="inline-block w-2 h-4 bg-amber-500 align-middle ml-0.5 animate-pulse" data-testid="boris-synthese-cursor" />}
                    </div>
                    <div className="mt-4 pt-3 border-t flex justify-end">
                      <Button onClick={() => setSendDialogFor(synthese)}
                              disabled={synthese.streaming || !synthese.id}
                              className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                              data-testid="boris-send-synthese-btn">
                        <Mail size={14} className="mr-1" /> Envoyer la synthèse par e-mail
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* History */}
            {tab === 'history' && (
              <div className="space-y-3" data-testid="boris-history">
                {history.length === 0 && <p className="text-sm text-gray-400 italic p-4">Aucune analyse enregistrée pour ce chantier.</p>}
                {history.map(h => (
                  <div key={h.id} className="bg-white rounded-xl border p-3" data-testid={`boris-history-${h.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{h.type}</span>
                      <span className="text-[10px] text-gray-400">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                      {h.photos_count > 0 && <span className="text-[10px] text-gray-500">· {h.photos_count} photo(s)</span>}
                    </div>
                    <details>
                      <summary className="text-xs text-[#0055FF] cursor-pointer">Voir le détail</summary>
                      <div className="mt-2"><MarkdownView content={h.result_markdown} /></div>
                    </details>
                  </div>
                ))}
              </div>
            )}
      </div>
      <SendEmailDialog open={!!sendDialogFor} analysis={sendDialogFor} onClose={() => setSendDialogFor(null)} />
    </AppShell>
  );
}
